import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useLocation, useParams } from "react-router-dom";
import { ConfigProvider } from "antd";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { capitalizeTitle } from "src/utils/helperFunctions";
import { faceFacts, feedFromTimeline, isCaptured, notCapturedOf, settingsFrom } from "./rules/fromTimeline";
import { reviewHeadline } from "./rules/proctoring";
import { ReviewClockProvider, floorAt, readAddress, recordedRuns, searchWith, useReviewClock } from "./clock";
import { ANALYSES, SEV_RANK, analysisOf, isSpokenQuestion, liveSpan } from "./constants";
import useReviewMedia from "./hooks/useReviewMedia";
import useReviewKeys from "./hooks/useReviewKeys";
import ReviewHeader from "./ReviewHeader";
import ReviewQueue from "./ReviewQueue";
import Evidence from "./Evidence";
import Player from "./Player/Player";
import SnapshotReel from "./SnapshotReel";
import Lightbox from "./Lightbox";
import Lanes from "./Lanes";
import AudioSummary from "./AudioSummary";
import "./ProctoringReview.scss";

/*
 * The proctoring review room: a full-screen layer over the report, on the same
 * route (?view=proctoring), so the report behind it is never fetched again.
 *
 *   the bar      the trust score ring, the verdict, the session's figures, the report's pills
 *   left         the review queue: each check and its evidence
 *   right        the recording (or the captures, where nothing was recorded),
 *                the timeline's lanes, and the audio in one line under them
 *
 * Every moment is on one clock (clock.js).
 */

const isFiniteNum = (x) => typeof x === "number" && Number.isFinite(x);

/** Everything the timeline holds, by key: the room shows an item's own data beside the rules' words. */
function sourcesOf(T) {
  const m = new Map();
  const add = (list) => (list || []).forEach((x) => { if (x && x.key) m.set(x.key, x); });
  add(T.captures); add(T.references); add(T.profiles); add(T.screens); add(T.integrity);
  const a = T.audio || {};
  add(a.markers);
  add(a.review && a.review.instances);
  return m;
}

/** What was recorded, in words, from the kinds of media the room loads. */
const recordingWords = (kinds) => {
  const has = (k) => kinds.includes(k);
  if (has("user") && has("screen")) return "Camera and screen";
  if (has("user")) return has("audio") ? "Camera and audio" : "Camera";
  if (has("screen")) return has("audio") ? "Screen and audio" : "Screen";
  if (has("audio")) return "Audio only";
  if (has("images")) return "Captures only";
  return "Not recorded";
};

/**
 * The stretches of the recording the player shows first (the camera, else the screen, else the audio): where the
 * room opens, and where a flag's lead-up stops.
 */
const primaryRuns = (T) => {
  const R = (T && T.recordings) || {};
  const kind = ["user", "screen", "audio"].find((k) => recordedRuns(R[k]).length);
  return kind ? recordedRuns(R[kind]) : [];
};

/** Where an item sits among the round's modules; before the first or between two, it says so. */
export const BEFORE_MODULES = { key: "pseudo:before", name: "Before the first module", pseudo: true };
export const BETWEEN_MODULES = { key: "pseudo:between", name: "Between modules", pseudo: true };
export function moduleAtTime(modules, t) {
  if (!modules.length) return null;
  const m = modules.find((x) => t >= x.from && t < x.to);
  if (m) return m;
  if (t < modules[0].from) return BEFORE_MODULES;
  if (t >= modules[modules.length - 1].to) return modules[modules.length - 1];
  return BETWEEN_MODULES;
}

/** The quiz question id in an item's question key ("q:<id>"), for the link to it in the report. */
export const questionIdOf = (key) => (typeof key === "string" && key.startsWith("q:") && key.length > 2 ? key.slice(2) : null);

/** A recording the timeline says exists: captured, or with chunks on the clock. */
const recordedKind = (presence, rec) => isCaptured(presence) || !!(rec && (Number(rec.chunks) > 0 || (Array.isArray(rec.sessions) ? rec.sessions.length : Number(rec.sessions) > 0)));

function Room({ timeline, reportData, linkConfig, positionConfig, isCandidateReport, canOpenQuestion, onClose }) {
  const c = useReviewClock();
  const location = useLocation();
  const { linkId, userId } = useParams();
  const T = timeline;
  const P = useMemo(() => T.presence || {}, [T]);

  /* ---- the checks ---- */
  const settings = useMemo(() => settingsFrom(linkConfig, positionConfig, P, T.tabs), [linkConfig, positionConfig, P, T]);
  const feed = useMemo(() => feedFromTimeline(T, { settings, reportData }), [T, settings, reportData]);
  const all = useMemo(() => feed.filter((x) => x.kind !== "video"), [feed]);
  const flagged = useMemo(() => all.filter((x) => x.flagged), [all]);
  const captures = useMemo(() => all.filter((x) => x.kind === "capture"), [all]);
  const screens = useMemo(() => all.filter((x) => x.kind === "screen"), [all]);
  const sources = useMemo(() => sourcesOf(T), [T]);
  const questions = useMemo(() => new Map((T.questions || []).map((q) => [q.key, q])), [T]);
  const modules = useMemo(() => (T.parts || []).filter((p) => p && isFiniteNum(p.from) && isFiniteNum(p.to) && p.to > p.from)
    .slice().sort((a, b) => a.from - b.from), [T]);
  const moduleAt = useCallback((t) => moduleAtTime(modules, t), [modules]);
  const moduleNameAt = useCallback((t) => { const m = moduleAt(t); return m ? m.name : ""; }, [moduleAt]);
  const on = (a) => (a.setting == null ? true : !!settings[a.setting]) || all.some((x) => analysisOf(x.kind) === a.k);
  const analyses = ANALYSES.filter(on);
  const off = ANALYSES.filter((a) => !on(a));
  const notCaptured = useMemo(() => notCapturedOf(T), [T]);
  const expected = useMemo(() => (T.questions || []).filter((q) => isSpokenQuestion(q) && isFiniteNum(q.from) && isFiniteNum(q.to)), [T]);
  const speech = useMemo(() => ((T.audio && T.audio.speech) || []).filter((x) => x && isFiniteNum(x.at) && Number(x.dur) > 0), [T]);
  const runs = useMemo(() => primaryRuns(T), [T]);
  const floorOf = useCallback((at) => floorAt(runs, at), [runs]);

  /* ---- media ---- */
  const kinds = useMemo(() => {
    const R = T.recordings || {};
    const k = [];
    if (recordedKind(P.userRecording, R.user)) k.push("user");
    if (recordedKind(P.screenRecording, R.screen)) k.push("screen");
    if (recordedKind(P.audioRecording, R.audio)) k.push("audio");
    if ((T.captures || []).length || (T.screens || []).length || (T.profiles || []).length || (T.references || []).length) k.push("images");
    return k;
  }, [T, P]);
  const media = useReviewMedia({ linkId, userId, kinds });

  const imageOf = useCallback((x) => {
    const src = sources.get(x.key) || {};
    return media.imageOf(x.key, src.imageUrl || src.snapshotLink || null);
  }, [sources, media]);
  const onImageError = useCallback(() => media.onError("images"), [media]);

  const status = (k) => (media.media[k] ? media.media[k].status : "loading");
  const videoSources = [["user", "Camera"], ["screen", "Screen"]]
    .filter(([k]) => kinds.includes(k) && media.clipsOf(k).length)
    .map(([k, n]) => ({ k, n, clips: media.clipsOf(k), gaps: ((T.recordings || {})[k] || {}).gaps || [] }));
  const audioMedia = kinds.includes("audio") && media.clipsOf("audio").length ? { clips: media.clipsOf("audio") } : null;
  const recorded = kinds.some((k) => k !== "images");
  /* video-stream could not be reached when the timeline was built: say so, not "nothing was recorded" */
  const unreachable = ["userRecording", "screenRecording", "audioRecording", "imageProctoring"].some((k) => P[k] === "unavailable");
  const mediaLoading = kinds.filter((k) => k !== "images").some((k) => ["loading", undefined].includes(status(k)) && !media.clipsOf(k).length);
  const hasMedia = videoSources.length > 0 || !!audioMedia;

  /* ---- the list ---- */
  const [scope, setScope] = useState("flagged");
  const [order, setOrder] = useState("time");
  const [filter, setFilter] = useState("any");
  const sel = readAddress(location.search).item;
  const matches = useCallback((x) => filter === "any" || analysisOf(x.kind) === filter, [filter]);
  const queue = useMemo(() => (scope === "flagged" ? flagged : all).filter(matches).slice()
    .sort((a, b) => (scope === "all" && order === "severity" ? SEV_RANK[a.severity] - SEV_RANK[b.severity] : 0) || a.at - b.at), [scope, flagged, all, matches, order]);
  const selected = all.find((x) => x.key === sel) || null;

  /* an address naming an item the list does not show (a clear one, another analysis): show it */
  useEffect(() => {
    if (!selected || queue.some((x) => x.key === selected.key)) return;
    setFilter("any");
    if (!selected.flagged) setScope("all");
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = useCallback((x) => {
    if (!x) return;
    c.seek(x.at, { lead: x.flagged && hasMedia, floor: floorOf(x.at), item: x.key, play: false });
  }, [c, hasMedia, floorOf]);
  const onToggle = useCallback((x) => {
    if (x.key === sel) c.setAddress({ item: null });
    else pick(x);
  }, [c, pick, sel]);
  const step = (d) => {
    if (!queue.length) return;
    const i = queue.findIndex((x) => x.key === sel);
    pick(queue[i < 0 ? (d > 0 ? 0 : queue.length - 1) : Math.max(0, Math.min(queue.length - 1, i + d))]);
  };

  /* ---- the lightbox ---- */
  const [zoom, setZoom] = useState(null);
  const zoomItem = zoom ? all.find((x) => x.key === zoom) : null;
  const zoomList = zoomItem && zoomItem.kind === "screen" ? screens : captures;
  const zoomIndex = zoomItem ? zoomList.findIndex((x) => x.key === zoom) : -1;
  const zoomStep = (d) => {
    const n = zoomList[Math.max(0, Math.min(zoomList.length - 1, zoomIndex + d))];
    if (n) { setZoom(n.key); pick(n); }
  };
  const zoomLines = (() => {
    if (!zoomItem) return [];
    const s = sources.get(zoomItem.key) || {};
    const v = s.verdict || {};
    return [v.type ? capitalizeTitle(v.type) : null, v.remark || (s.criticalReason ? String(s.criticalReason) : null), zoomItem.note || null].filter(Boolean);
  })();

  useReviewKeys(true, {
    zoomOpen: !!zoom,
    closeZoom: () => setZoom(null),
    zoomStep,
    close: onClose,
    next: () => step(1),
    prev: () => step(-1),
    toggle: c.toggle,
    zoom: selected && (selected.kind === "capture" || selected.kind === "screen") ? () => setZoom(selected.key) : undefined,
  });

  /* ---- the figures ---- */
  const headline = reviewHeadline(feed);
  /* the round's tab switches: the timeline's total, the report's, else the per-question counts; null when none is known */
  const perQuestionTabs = (T.questions || []).filter((q) => q.tabSwitches != null && Number.isFinite(Number(q.tabSwitches)));
  const tabsTotal = perQuestionTabs.length ? perQuestionTabs.reduce((a, q) => a + Math.max(0, Number(q.tabSwitches)), 0) : null;
  /* how many sessions the round took: the most any recording type has */
  const sessionsOf = (r) => (r ? (Array.isArray(r.runs) ? r.runs.length : Array.isArray(r.sessions) ? r.sessions.length : Number(r.sessions) || 0) : 0);
  const restarts = all.filter((x) => x.kind === "session");
  const facts = {
    length: c.length,
    faces: faceFacts(all),
    sessions: Math.max(sessionsOf((T.recordings || {}).user), sessionsOf((T.recordings || {}).screen), sessionsOf((T.recordings || {}).audio), restarts.length + 1),
    tabs: {
      count: T.tabs && T.tabs.total != null ? T.tabs.total : (reportData && reportData.tabSwitched != null ? reportData.tabSwitched : tabsTotal),
      limit: settings.tabLimit,
    },
    recording: recordingWords(kinds),
  };
  const liveKeys = new Set(c.engaged ? all.filter((x) => c.near(x.at, liveSpan(x))).map((x) => x.key) : []);
  const countFor = (k) => all.filter((x) => k === "any" || analysisOf(x.kind) === k).length;

  const renderEvidence = (x) => {
    const src = sources.get(x.key) || {};
    const s = x.kind === "profile"
      ? { ...src, profileUrl: media.imageOf(`${x.key}:profile`, src.profileUrl), sessionUrl: media.imageOf(`${x.key}:session`, src.sessionUrl) }
      : src;
    const qid = canOpenQuestion ? questionIdOf(x.questionKey) : null;
    return (
      <>
        <Evidence
          x={x}
          src={s}
          captures={captures}
          imageOf={imageOf}
          onImageError={onImageError}
          onPick={pick}
          onZoom={setZoom}
          audio={T.audio}
          question={x.questionKey ? questions.get(x.questionKey) : null}
          hasAudio={hasMedia}
          showReadiness={!!(positionConfig && positionConfig.trustScorePersonalityAnalysis)}
          floorOf={floorOf}
        />
        {qid && (
          <Link
            className="proctoring-review__to-question"
            to={{ pathname: location.pathname, search: searchWith(location.search, { view: null, t: null, item: null, question: qid }), hash: location.hash }}
          >
            {`Open ${x.questionLabel || "the question"} in the report →`}
          </Link>
        )}
      </>
    );
  };

  return (
    <>
      <ReviewHeader
        person={reportData && reportData.user ? reportData.user.name || reportData.user.email : ""}
        position={reportData && reportData.position ? reportData.position.name : ""}
        headline={headline}
        facts={facts}
        trustScore={reportData ? reportData.trustScore : null}
        positionConfig={positionConfig}
        linkConfig={linkConfig}
        cutOffCleared={reportData ? reportData.cutOffCleared : undefined}
        cameraSupported={reportData ? reportData.cameraSupported : undefined}
        approxClock={T.clock && T.clock.offsetMs == null && recorded}
        unreachable={unreachable}
        isCandidateReport={isCandidateReport}
        onClose={onClose}
      />
      <div className="proctoring-review__body">
        <ReviewQueue
          queue={queue}
          allCount={all.length}
          flaggedCount={flagged.length}
          scope={scope}
          onScope={setScope}
          filter={filter}
          onFilter={setFilter}
          order={order}
          onOrder={setOrder}
          sel={sel}
          liveKeys={liveKeys}
          moduleNameAt={moduleNameAt}
          thumbOf={imageOf}
          onToggle={onToggle}
          onImageError={onImageError}
          renderEvidence={renderEvidence}
          countFor={countFor}
        />
        <aside className="proctoring-review__right" aria-label={recorded ? "Recording" : "Captures"}>
          <div className="proctoring-review__stage">
            {recorded
              ? (
                <Player
                  sources={videoSources}
                  audio={audioMedia}
                  modules={modules}
                  moduleAt={moduleAt}
                  flagged={flagged}
                  restarts={restarts}
                  onFlag={pick}
                  onMediaError={media.onError}
                  loading={mediaLoading}
                />
              )
              : captures.length > 0
                ? (
                  <SnapshotReel
                    captures={captures}
                    sel={sel}
                    imageOf={imageOf}
                    onImageError={onImageError}
                    moduleAt={moduleAt}
                    onPick={pick}
                    onZoom={setZoom}
                  />
                )
                : (
                  <div className="proctoring-review__nothing">
                    {unreachable
                      ? "The recordings and captures could not be reached just now. Close the review and open it again in a few minutes."
                      : "Nothing was recorded and no captures were taken in this round."}
                  </div>
                )}
            <section className="proctoring-review__timeline" aria-label="Timeline">
              <Lanes
                modules={modules}
                analyses={analyses}
                off={off}
                items={all}
                expected={expected}
                speech={speech}
                sel={sel}
                onPick={pick}
                filter={filter}
                onFilter={(k) => {
                  const has = flagged.some((x) => analysisOf(x.kind) === k);
                  setFilter(filter === k ? "any" : k);
                  setScope(has ? "flagged" : "all");
                }}
              />
              <AudioSummary
                audio={T.audio || null}
                audioProctoringReport={reportData ? reportData.audioProctoringReport : null}
                notCaptured={notCaptured}
              />
            </section>
          </div>
        </aside>
      </div>
      {zoomItem && (
        <Lightbox
          item={zoomItem}
          url={imageOf(zoomItem)}
          index={zoomIndex}
          total={zoomList.length}
          module={moduleNameAt(zoomItem.at)}
          lines={zoomLines}
          onStep={zoomStep}
          onClose={() => setZoom(null)}
          onImageError={onImageError}
        />
      )}
    </>
  );
}

/**
 * @param {Object} props
 * @param {Object|null} props.reportData      the page's one report-data (never fetched again here)
 * @param {Object|null} props.linkConfig
 * @param {Object|null} props.positionConfig
 * @param {boolean} props.configsReady        both config reads have settled
 * @param {{status: string, timeline: Object|null, error: string|null, retry: Function}} props.timelineState
 * @param {boolean} [props.isCandidateReport]
 * @param {boolean} [props.canOpenQuestion]  an item can link to its question in the report (not in the PDF or mini views)
 * @param {Function} props.onClose
 */
export default function ProctoringReview({ reportData, linkConfig, positionConfig, configsReady, timelineState, isCandidateReport = false, canOpenQuestion = false, onClose }) {
  const rootRef = useRef(null);

  /* the page behind does not scroll while the room is open */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  /* the room takes the focus, so the keys and screen readers start in it */
  useEffect(() => {
    if (rootRef.current) rootRef.current.focus({ preventScroll: true });
  }, []);

  const getPopupContainer = useCallback(() => rootRef.current || document.body, []);
  const ready = !!reportData && configsReady && timelineState.status === "ready" && !!timelineState.timeline;
  /* while it loads (or failed), Esc still closes the room; once ready, the room's own keys take over */
  useReviewKeys(!ready, { close: onClose });

  const shell = (children) => (
    <>
      <header className="proctoring-review__bar">
        <div className="proctoring-review__verdict">
          <span className="proctoring-review__who">Proctoring</span>
        </div>
        <button type="button" className="proctoring-review__bar-btn" onClick={onClose} aria-label="Close proctoring"><CloseOutlined /></button>
      </header>
      <div className="proctoring-review__wait">{children}</div>
    </>
  );

  let content;
  if (timelineState.status === "error") {
    content = shell(
      <>
        <b>The proctoring review could not be loaded.</b>
        <span>{timelineState.error}</span>
        <button type="button" onClick={timelineState.retry}>Try again</button>
      </>
    );
  } else if (!ready) {
    content = shell(<><LoadingOutlined /> <span>Loading the proctoring review</span></>);
  } else {
    content = (
      <ReviewClockProvider
        length={timelineState.timeline.clock.length}
        start={(primaryRuns(timelineState.timeline)[0] || {}).from ?? null}
      >
        <Room
          timeline={timelineState.timeline}
          reportData={reportData}
          linkConfig={linkConfig}
          positionConfig={positionConfig}
          isCandidateReport={isCandidateReport}
          canOpenQuestion={canOpenQuestion}
          onClose={onClose}
        />
      </ReviewClockProvider>
    );
  }

  return ReactDOM.createPortal(
    <ConfigProvider getPopupContainer={getPopupContainer}>
      <div
        className="proctoring-review"
        ref={rootRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Proctoring review"
        data-view="proctoring"
      >
        {content}
      </div>
    </ConfigProvider>,
    document.body
  );
}
